<!--
TestDomainBanner.vue
Displays a warning banner when the app is accessed via the test domain (appv5.ovationcxm.app).
This banner warns users that some features may not work properly on the test domain and provides
a link to navigate to the production domain (app.ovationcxm.com). The banner can be dismissed and
the dismissal state is persisted in localStorage.
-->
<script lang="ts" setup>
import Message from '@services/ui-component-library/components/BravoMessage.vue';
import { useStorage } from '@vueuse/core';
import { computed } from 'vue';

// Check if we're on the test domain
const isTestDomain = computed(() => {
  return window.location.hostname === 'appv5.ovationcxm.app';
});

// Use VueUse's useStorage to manage dismissal state per domain
const isDismissed = useStorage('test_domain_banner_dismissed', false);

// Function to dismiss the banner
const dismissBanner = () => {
  isDismissed.value = true;
};

// Function to navigate to production URL
const goToProduction = () => {
  window.location.href = 'https://app.ovationcxm.com';
};
</script>

<template>
  <div v-if="isTestDomain && !isDismissed" class="test-domain-banner">
    <Message severity="warn" :closable="true" @close="dismissBanner">
      <div class="banner-content">
        <i class="pi pi-exclamation-triangle" style="margin-right: 0.5rem"></i>
        <span>
          You are viewing <strong>appv5.ovationcxm.app</strong> (test domain). 
          Some features may not work properly. Please use 
          <a href="https://app.ovationcxm.com" class="prod-link" @click.prevent="goToProduction">
            app.ovationcxm.com
          </a> 
          for the full experience.
        </span>
      </div>
    </Message>
  </div>
</template>

<style scoped>
.test-domain-banner {
  position: sticky;
  top: 0;
  z-index: 1001;
  margin: 0;
}

.banner-content {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.prod-link {
  color: #1e40af;
  text-decoration: underline;
  font-weight: 600;
  cursor: pointer;
}

.prod-link:hover {
  color: #1e3a8a;
  text-decoration: none;
}
</style>

